// Aggregations over time entries, shared by the dashboard widgets, reports and
// client pages so every screen computes the same numbers the same way.
//
// Conventions (same as the server's /api/analytics):
//   minutes  = rounded_duration_minutes ?? duration_minutes
//   amount   = total_amount (fallback hours × rate)
//   revenue  = amount of billable entries (status ≠ non_billable)
//   outstanding = amount − amount_paid for open / partial entries

import { entryAmount, entryMinutes, normalizeStatus, parseDate, isoDate } from './format';
import { buckets as makeBuckets, inRange, type Bucket, type PeriodRange } from './periods';
import type { TimeEntry } from './types';

export interface EntryFilter {
  range?: Pick<PeriodRange, 'start' | 'end'> | null;
  clientIds?: number[] | null;
  activityIds?: number[] | null;
  /** true = billable only, false = non-billable only, null = both */
  billable?: boolean | null;
  statuses?: Array<'open' | 'partial' | 'paid' | 'non_billable'> | null;
  search?: string | null;
}

export function filterEntries(entries: TimeEntry[], f: EntryFilter = {}): TimeEntry[] {
  const q = f.search?.trim().toLocaleLowerCase() || '';
  const clients = f.clientIds?.length ? new Set(f.clientIds) : null;
  const activities = f.activityIds?.length ? new Set(f.activityIds) : null;
  const statuses = f.statuses?.length ? new Set(f.statuses) : null;
  return entries.filter((e) => {
    if (f.range && !inRange(e.date || e.start_time, f.range)) return false;
    if (clients && !clients.has(e.client_id as number)) return false;
    if (activities && !activities.has(e.activity_id as number)) return false;
    const status = normalizeStatus(e.payment_status);
    if (f.billable === true && status === 'non_billable') return false;
    if (f.billable === false && status !== 'non_billable') return false;
    if (statuses && !statuses.has(status)) return false;
    if (q) {
      const hay = `${e.description || ''} ${e.client_name || ''} ${e.activity_name || ''} ${e.invoice_reference || ''}`.toLocaleLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export interface Totals {
  count: number;
  minutes: number;
  billableMinutes: number;
  nonBillableMinutes: number;
  revenue: number;
  paid: number;
  outstanding: number;
  outstandingMinutes: number;
  /** Revenue per billable hour. */
  avgRate: number;
  days: number;
}

export function totals(entries: TimeEntry[]): Totals {
  const t: Totals = { count: 0, minutes: 0, billableMinutes: 0, nonBillableMinutes: 0, revenue: 0, paid: 0, outstanding: 0, outstandingMinutes: 0, avgRate: 0, days: 0 };
  const days = new Set<string>();
  for (const e of entries) {
    const min = entryMinutes(e);
    const status = normalizeStatus(e.payment_status);
    t.count += 1;
    t.minutes += min;
    days.add(e.date || (e.start_time || '').slice(0, 10));
    if (status === 'non_billable') {
      t.nonBillableMinutes += min;
      continue;
    }
    const amount = entryAmount(e);
    const paid = Math.min(amount, Number(e.amount_paid) || 0);
    t.billableMinutes += min;
    t.revenue += amount;
    if (status === 'paid') t.paid += amount;
    else {
      t.paid += paid;
      t.outstanding += Math.max(0, amount - paid);
      t.outstandingMinutes += min;
    }
  }
  t.avgRate = t.billableMinutes > 0 ? t.revenue / (t.billableMinutes / 60) : 0;
  t.days = days.size;
  return t;
}

export interface Group {
  key: string;
  id: number | null;
  label: string;
  entries: TimeEntry[];
  totals: Totals;
}

export function groupBy(entries: TimeEntry[], by: 'client' | 'activity' | 'day' | 'week' | 'month' | 'status'): Group[] {
  const map = new Map<string, { id: number | null; label: string; entries: TimeEntry[] }>();
  for (const e of entries) {
    let key: string;
    let id: number | null = null;
    let label: string;
    switch (by) {
      case 'client':
        id = e.client_id;
        key = String(id ?? 'none');
        label = e.client_name || '—';
        break;
      case 'activity':
        id = e.activity_id;
        key = String(id ?? 'none');
        label = e.activity_name || '—';
        break;
      case 'day':
        key = e.date || (e.start_time || '').slice(0, 10);
        label = key;
        break;
      case 'week': {
        const d = parseDate(e.date) || new Date();
        const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - ((d.getDay() + 6) % 7));
        key = isoDate(monday);
        label = key;
        break;
      }
      case 'month':
        key = (e.date || '').slice(0, 7);
        label = key;
        break;
      default:
        key = normalizeStatus(e.payment_status);
        label = key;
    }
    let g = map.get(key);
    if (!g) map.set(key, (g = { id, label, entries: [] }));
    g.entries.push(e);
  }
  return [...map.entries()].map(([key, g]) => ({ key, id: g.id, label: g.label, entries: g.entries, totals: totals(g.entries) }));
}

export type Metric = 'hours' | 'billable_hours' | 'revenue' | 'outstanding' | 'paid' | 'count';

export function metricOf(t: Totals, metric: Metric): number {
  switch (metric) {
    case 'hours': return t.minutes / 60;
    case 'billable_hours': return t.billableMinutes / 60;
    case 'revenue': return t.revenue;
    case 'outstanding': return t.outstanding;
    case 'paid': return t.paid;
    case 'count': return t.count;
  }
}

export interface Series {
  buckets: Bucket[];
  values: number[];
}

/** Values of a metric per chart bucket (day / week / month) within a range. */
export function series(entries: TimeEntry[], range: PeriodRange, metric: Metric, weekStart = 1, fixedBuckets?: Bucket[]): Series {
  const bs = fixedBuckets || makeBuckets(range, weekStart);
  const perBucket: TimeEntry[][] = bs.map(() => []);
  for (const e of entries) {
    const d = parseDate(e.date || e.start_time);
    if (!d) continue;
    // Binary search would be faster; bucket counts are small (≤ 31).
    for (let i = 0; i < bs.length; i++) {
      if (d >= bs[i].start && d < bs[i].end) {
        perBucket[i].push(e);
        break;
      }
    }
  }
  return { buckets: bs, values: perBucket.map((list) => metricOf(totals(list), metric)) };
}

/** Same buckets shifted back to the comparison range (for "vs previous period"). */
export function comparisonSeries(entries: TimeEntry[], current: Bucket[], previous: PeriodRange, metric: Metric): number[] {
  if (!current.length) return [];
  const shift = current[0].start.getTime() - previous.start.getTime();
  const shifted = current.map((b) => ({ ...b, start: new Date(b.start.getTime() - shift), end: new Date(b.end.getTime() - shift) }));
  return series(entries, previous, metric, 1, shifted).values;
}

/** Minutes per calendar date ('YYYY-MM-DD' → minutes), for heatmaps and streaks. */
export function minutesPerDay(entries: TimeEntry[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const e of entries) {
    const key = e.date || (e.start_time || '').slice(0, 10);
    out[key] = (out[key] || 0) + entryMinutes(e);
  }
  return out;
}

/** Consecutive work days (counting back from today, skipping non-work days). */
export function streak(perDay: Record<string, number>, workDays: number[]): number {
  let n = 0;
  const d = new Date();
  // Today counts only once something is logged.
  if (!perDay[isoDate(d)]) d.setDate(d.getDate() - 1);
  for (let i = 0; i < 400; i++) {
    const key = isoDate(d);
    if (perDay[key]) n += 1;
    else if (workDays.includes(d.getDay())) break;
    d.setDate(d.getDate() - 1);
  }
  return n;
}

/** Percentage change, null when there is nothing to compare against. */
export function change(current: number, previous: number | null | undefined): number | null {
  if (previous === null || previous === undefined) return null;
  if (previous === 0) return current === 0 ? 0 : 100;
  return ((current - previous) / Math.abs(previous)) * 100;
}

/** Target hours for a range, from the weekly target spread over work days. */
export function targetHours(range: Pick<PeriodRange, 'start' | 'end'>, weeklyTarget: number, workDays: number[]): number {
  if (!workDays.length || !weeklyTarget) return 0;
  const perDay = weeklyTarget / workDays.length;
  let total = 0;
  const end = range.end.getTime();
  for (let d = new Date(range.start); d.getTime() < end; d.setDate(d.getDate() + 1)) {
    if (workDays.includes(d.getDay())) total += perDay;
    if (total > 1e6) break;
  }
  return total;
}
